import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import {
  CellClickEvent,
  DataBindingDirective,
  GridDataResult,
  SelectionEvent
} from "@progress/kendo-angular-grid";
import {
  CompositeFilterDescriptor,
  process,
  SortDescriptor
} from "@progress/kendo-data-query";
import { CookieService } from "ngx-cookie-service";
import { BehaviorSubject } from "rxjs";
import { I18nService } from "src/app/i18n/services";
import { Circulation, CirculationId, IGridOptions } from "src/app/interfaces";
import { isNullOrWhiteSpace } from "src/app/interfaces/string";
import { NotificationFacade } from "src/app/notifications/services/notification.facade";

/**
 * Inputs:
 * showOutCome: set to true, to show the "outcome" column
 * data: the list of circulations to show.
 * enableSelect: enables all select features.
 *
 * Outputs:
 * cellClick: Emits the circ-id when a row is clicked. Does NOT emit, when select is enabled.
 * selectionChanged: Emits a list of circs that were selected. Does ONLY emit when select is enabled.
 */
@Component({
    selector: "circ-grid",
    templateUrl: "./grid.component.html",
    styleUrls: ["./grid.component.scss"],
    standalone: false
})
export class GridComponent implements OnInit {
  @ViewChild(DataBindingDirective) dataBinding: DataBindingDirective;
  public gridView$ = new BehaviorSubject<GridDataResult>(null);
  public sort: SortDescriptor[];
  public gridOptions: IGridOptions;
  public filterValue: string = null;
  public sortSettings = {
    allowUnsort: true,
    mode: "single"
  };
  public selectSettings: unknown = null;
  public gridSelectedKeys: number[] = [];
  private sortSettingsCookieTitle = "circSortDescriptor";
  @Output() cellClick = new EventEmitter<CirculationId>();
  @Output() selectionChanged = new EventEmitter<Circulation[]>();
  private _data = new BehaviorSubject<Circulation[]>([]);
  private _enableSelect = false;
  @Input() showOutcome = false;
  @Input()
  set enableSelect(value: boolean) {
    this._enableSelect = value;
    if (value) {
      this.selectSettings = {
        cell: false,
        checkboxOnly: false,
        enabled: true,
        mode: "multiple"
      };
    } else {
      this.selectSettings = {
        enabled: false
      };
    }
  }
  get enableSelect(): boolean {
    return this._enableSelect;
  }
  @Input()
  set data(value: Circulation[]) {
    this._data.next(value);
  }
  get data(): Circulation[] {
    return this._data.getValue();
  }

  constructor(
    private cookieService: CookieService,
    private notificationFacade: NotificationFacade,
    private i18nService: I18nService
  ) {}

  ngOnInit(): void {
    this._data.subscribe((d) => {
      this.gridOptions = {
        data: d
      };

      this.setSortSettings();
      this.updateGridView();
    });
  }
  private setSortSettings(): void {
    if (this.cookieService.check(this.sortSettingsCookieTitle)) {
      const cookie: string = this.cookieService.get(
        this.sortSettingsCookieTitle
      );
      const sortSettings = JSON.parse(cookie);
      this.sort = sortSettings;
    } else {
      this.sort = [
        {
          field: "dueDate",
          dir: "desc"
        }
      ];
    }
  }

  private updateGridView() {
    if (!this.data) {
      this.gridView$.next(null);
      return;
    }

    this.gridView$.next(
      process(this.data, {
        sort: this.sort,
        filter: this.getFilter(this.filterValue)
      })
    );
  }

  private sendUserFeedBack(translator) {
    this.i18nService.get(translator).subscribe((t) => {
      this.notificationFacade.success(t, 2000);
    });
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.cookieService.set(this.sortSettingsCookieTitle, JSON.stringify(sort), {
      expires: 30,
      path: "/"
    });
    this.sendUserFeedBack("feedback_save_successful");
    this.updateGridView();
  }

  public onFilter(input: string): void {
    this.filterValue = input;
    this.updateGridView();
  }

  private getFilter(value: string): CompositeFilterDescriptor {
    if (isNullOrWhiteSpace(value)) {
      return null;
    }

    return {
      logic: "or",
      filters: [
        {
          field: "title",
          operator: "contains",
          value: value
        },
        {
          field: "payload.demand",
          operator: "contains",
          value: value
        },
        {
          field: "dueDate",
          operator: "eq",
          value: value
        },
        {
          field: "outcome",
          operator: "eq",
          value: value
        }
      ]
    };
  }

  public onCellClick(e: CellClickEvent): void {
    if (this._enableSelect) {
      return;
    }

    const circ = e.dataItem as Circulation;

    this.cellClick.emit(circ.id);
  }

  public onSelectionChange(e: SelectionEvent) {
    if (!this._enableSelect) {
      return;
    }

    const selected = e.selectedRows.map((r) => r.dataItem as Circulation);

    this.selectionChanged.emit(selected);
  }
}
